import { DateValue } from '@internationalized/date';
import { MCCSummaryItem, Photo, UpdateAction } from '@types';
import {
	convertDateToCalendarDate,
	emptyToUndefined,
	FileUploadItem,
	FileUploadOptionalSchema,
	FileUploadSchema,
	FormLabels,
	latitudeMessage,
	latitudeRegExp,
	longitudeMessage,
	longitudeRegExp,
	maxLengthErrorMsg,
	minLengthErrorMsg,
	naNToUndefined,
	NestedArrayForm,
	numberValidationMessage,
	optionRequiredMessage,
	requiredMsg,
	safeAnnotationTextErrorMessage,
	safeAnnotationTextRegExp,
	sizeRangeValidationMessage,
	standardTextOnlyMessage,
	standardTextOnlyRegExp,
	widthRangeValidationMessage,
} from '@utils';
import * as Yup from 'yup';

import {
	DeviceReadingUnitMeasureOption,
	DeviceTypeOption,
	DocumentsForClientOption,
	InspectionOutcomeOption,
	makeAndModelOther,
	makeOther,
	modelOther,
	ReasonForInspectionOption,
	SystemInstallationConfigurationOption,
} from './moduleAFormOptionsData';

/*
	Declaration of Yup custom functions
*/
declare module 'yup' {
	interface StringSchema {
		requiredWhen(dependantFieldName: string, matchValues: string | boolean | Array<string | boolean>, requiredMsg: string): this; // prettier-ignore
		requiredWhenAny(dependantFields: string[], matchValues: Array<string | boolean>, requiredMsg: string): this; // prettier-ignore
		isIntegerWithinRangeStr(min: number, max: number, errorMsg: string): this;
		isMinNumberStr(minNumber: number, errorMsg: string): this;
	}
	interface BooleanSchema {
		requiredWhen(dependantFieldName: string, matchValues: string | boolean | Array<string | boolean>, requiredMsg: string): this; // prettier-ignore
		requiredWhenAny(dependantFields: string[], matchValues: Array<string | boolean>, requiredMsg: string): this; // prettier-ignore
	}
	interface NumberSchema {
		requiredWhen(dependantFieldName: string, matchValues: string | boolean | Array<string | boolean>, requiredMsg: string): this; // prettier-ignore
		requiredWhenAny(dependantFields: string[], matchValues: Array<string | boolean>, requiredMsg: string): this; // prettier-ignore
	}
}

/*
	Implementation of Yup custom functions
*/
Yup.addMethod(
	Yup.Schema,
	'requiredWhen',
	function (
		dependantField: string,
		matchValues: string | boolean | Array<string | boolean>,
		requiredMsg: string,
	) {
		const matchValuesArray: Array<string | boolean> = Array.isArray(matchValues) ? Array.from(matchValues) : [matchValues]; // prettier-ignore
		return this.when(dependantField, {
			is: (val: string) => matchValuesArray.includes(val),
			then: schema => schema.required(requiredMsg),
			otherwise: schema => schema.notRequired(),
		});
	},
);
Yup.addMethod(
	Yup.Schema,
	'requiredWhenAny',
	function (dependantFields: string[], matchValues: Array<string | boolean>, requiredMsg: string) {
		return this.when(dependantFields, (values, schema) => {
			const isRequired =
				values && values.some((value, index) => values[index] === matchValues[index]);
			return isRequired ? schema.required(requiredMsg) : schema.notRequired();
		});
	},
);

Yup.addMethod(Yup.string, 'isMinNumberStr', function (minNumber: number, errorMsg: string) {
	return this.test('min-number-str', errorMsg, function (value) {
		if (!value) return true;
		const numValue = parseFloat(value);
		return numValue >= minNumber;
	});
});

Yup.addMethod(
	Yup.string,
	'isIntegerWithinRangeStr',
	function (min: number, max: number, errorMsg: string) {
		return this.test('range-number-str', errorMsg, function (value) {
			if (!value) return true;
			const numValue = parseInt(value);
			return numValue >= min && numValue <= max;
		});
	},
);

// LENGTH RESTRICTIONS
export const coordinateDecimals = 5;
export const serialNumberMaxLength = 20;
export const otherMakeOrModelMaxLength = 20;
const readingValueDigitsOnlyMaxLength = 12; // does not include decimal point character
export const readingValueMaxLength = (readingValue: unknown) =>
	String(readingValue).includes('.')
		? readingValueDigitsOnlyMaxLength + 1
		: readingValueDigitsOnlyMaxLength;
export const explanationMinLength = 10;
export const explanationMaxLength = 2000;
export const moduleAForms02And03List2Max = 10;
export const meterCaseCodeLength = 11;
export const maxNumberOfMeters = 20;
export const sizeDecimals = 0;
export const widthDecimals = 2;
export const widthMaxLength = 6 + widthDecimals;

// NUMBER VALUES
export const modularMeteringSystemSizeMinValue = 601;
export const maxSizeValue = 9999;

// DATE RESTRICTIONS
export const MAX_INSPECTION_CALENDAR_DATE: DateValue = convertDateToCalendarDate(new Date());

// VALIDATIONS REGULAR EXPRESSIONS
const meterCertCaseCodeRegExp = /^[A-Za-z0-9]{3}-[A-Za-z0-9]{3}-[A-Za-z0-9]{3}$/;
const anyCharacterNvarcharRegExp = standardTextOnlyRegExp;
const readingRegExp = /^\d*(\.\d+)?$/;
const widthRegExp = /^(?!0+(\.0{0,2})?$)(\d{1,4})(\.\d{0,2})?$/; // 4 digits before decimal, optional 2 digits after

// VALIDATION ERROR MESSAGES
const anyCharacterNvarcharErrorMsg = `It ${standardTextOnlyMessage}`;
const dateErrorMsg = 'Select a date';
const meterCertCaseCodeErrorMsg =
	'Must be a valid code with 9 letters and/or numbers splitted by a hyphen each 3 characters (e.g. ABC-123-xyz)';
const lengthErrorMsg = (length: number): string => `Must be ${length} characters`;
const explanationErrorMsg = (maxLength: number): string =>
	`You are required to provide an answer. Max character limit ${maxLength} characters.`;
const additionalMetersErrorMsg = `Please enter a numeric value between 1 and ${maxNumberOfMeters}.`;
const maxNumberErrorMsg = (max: number, pluralTerm: string): string =>
	`Maximum of ${max} ${pluralTerm} can be added.`;
const optionErrorMsg = 'Must be a valid option';
export const imageRequiredErrorMsg = 'Upload a photograph. File size limit 20MB.';
const finalDeclarationRequiredMsg = 'You must provide the declaration';
const modularMeteringConfigErrorMsg = (minValue: number) => `Modular metering systems must be installed in size configurations greater than ${minValue - 1}mm.`; // prettier-ignore
const sensorRequiredErrorMsg = 'You must add at least one sensor to the modular meter.';

// GENERAL FIELDS VALIDATIONS (required not included)
const deviceType = Yup.string();
const make = Yup.string();
const model = Yup.string();
const makeAndModel = Yup.string();
const otherMakeOrModel = Yup.string()
	.transform(emptyToUndefined)
	.matches(anyCharacterNvarcharRegExp, anyCharacterNvarcharErrorMsg)
	.max(otherMakeOrModelMaxLength, maxLengthErrorMsg(otherMakeOrModelMaxLength));
const serialNumber = Yup.string()
	.transform(emptyToUndefined)
	.max(serialNumberMaxLength, maxLengthErrorMsg(serialNumberMaxLength));
const size = Yup.number()
	.transform(naNToUndefined)
	.min(1, sizeRangeValidationMessage)
	.max(maxSizeValue, sizeRangeValidationMessage);
const readingValue = Yup.string()
	.transform(emptyToUndefined)
	.matches(readingRegExp, numberValidationMessage)
	.test('maxLength', `Maximum length is ${readingValueDigitsOnlyMaxLength} digits`, value => {
		if (value != undefined) {
			return value.length <= readingValueMaxLength(value);
		} else {
			return true;
		}
	});
const readingUnit = Yup.string();
const widthValue = Yup.string()
	.transform(emptyToUndefined)
	.matches(widthRegExp, widthRangeValidationMessage);

export interface ActionableFormValues {
	id?: string;
	action?: UpdateAction;
}
export const actionableSchema: Yup.ObjectSchema<ActionableFormValues> = Yup.object({
	id: Yup.string().optional(),
	action: Yup.mixed<UpdateAction>().optional(),
});

// SUBFORMS
// Subform 01 - Reason For Inspection
export interface ModuleAForm01Values {
	field1: string;
	field2: ReasonForInspectionOption;
	field3?: boolean;
	field4?: boolean;
	field5?: boolean;
	field6?: number;
}

export const moduleAForm01Labels: FormLabels<ModuleAForm01Values> = {
	field1: 'Meter case code',
	field2: 'Select the reason for inspection',
	field3: 'Are you replacing an existing meter?',
	field4: 'Is this client information correct?',
	field5: 'Are you certifying more than one new meter for this client?',
	field6: 'How many additional new meter inspection forms do you require for this client (not including this one)?', // prettier-ignore
};

export const moduleAForm01SummaryLabels: FormLabels<ModuleAForm01Values> = {
	field1: 'Meter case code:',
	field2: 'Reason for inspection:',
	field3: 'Are you replacing an existing meter?',
	field4: 'Client correct?',
	field5: 'Multiple meters?',
	field6: 'Number of meters',
};
export const meterCaseCodeSchema = Yup.string()
	.required('The meter certification case code is required')
	.matches(meterCertCaseCodeRegExp, meterCertCaseCodeErrorMsg)
	.length(11, lengthErrorMsg(11));

export const hasOutcomeKeyName = 'hasOutcome';
export const hasLinkedFormKeyName = 'hasLinkedForm';
export const caseTypeKeyName = 'caseType';
const contextHasOutcome = '$' + hasOutcomeKeyName;
const contextHasLinkedForm = '$' + hasLinkedFormKeyName;
const contextCaseType = '$' + caseTypeKeyName;
export const moduleAForm01Schema: Yup.ObjectSchema<ModuleAForm01Values> = Yup.object().shape({
	field1: meterCaseCodeSchema,
	field2: Yup.mixed<ReasonForInspectionOption>()
		.oneOf(Object.values(ReasonForInspectionOption) as ReasonForInspectionOption[], optionErrorMsg)
		.required(optionRequiredMessage),
	field3: Yup.boolean().when([contextCaseType], {
		is: (contextCaseTypeValue: MCCSummaryItem.caseType) => {
			return contextCaseTypeValue === MCCSummaryItem.caseType.METER_VALIDATION;
		},
		then: schema => schema.required(optionRequiredMessage),
		otherwise: schema => schema.notRequired(),
	}),
	field4: Yup.boolean().required(optionRequiredMessage),
	field5: Yup.boolean().when(
		['field4', contextCaseType, contextHasOutcome, contextHasLinkedForm],
		([field4Value, caseTypeValue, hasOutcome, hasLinkedForm], schema) => {
			const isRequired =
				field4Value === true &&
				caseTypeValue === MCCSummaryItem.caseType.NEW_METER &&
				hasOutcome === false &&
				hasLinkedForm === false;
			console.log(isRequired, );
			return isRequired ? schema.required(optionRequiredMessage) : schema.notRequired();
		},
	),
	field6: Yup.number()
		.transform(naNToUndefined)
		.when('field5', {
			is: (val: boolean) => val === true,
			then: schema =>
				schema
					.required(requiredMsg(moduleAForm01SummaryLabels.field6))
					.min(1, additionalMetersErrorMsg)
					.max(maxNumberOfMeters, additionalMetersErrorMsg),
			otherwise: schema => schema.notRequired(),
		}),
});

// Subform 02 and 03 - New and Existing Meter
// Subform 02_1 and 03_1 (for field as nested array form)
export interface SensorFormValues extends ActionableFormValues {
	field2: string;
	field3?: string;
	field4?: string;
	field5: string;
	field6: string;
	field7: DeviceReadingUnitMeasureOption;
	field8: string;
	field9?: number;
	field10?: string;
	field11?: string;
}
export type SensorFormValuesInFormArray = SensorFormValues & { id: string };
export const sensorShortLabels: FormLabels<Omit<SensorFormValues, keyof ActionableFormValues>> = {
	field2: 'Make model',
	field3: 'Make (other)',
	field4: 'Model (other)',
	field5: 'Serial number',
	field6: 'Sensor reading', // (uplift)
	field7: 'Unit of measure', // (uplift)
	// field6: 'Reading',
	// field7: 'Reading unit of measure',
	field8: 'Installation type',
	field9: 'Pipe size ⌀ (mm)',
	field10: 'Width',
	field11: 'Width unit of measure',
};
export const sensorLabels: FormLabels<Omit<SensorFormValues, keyof ActionableFormValues>> = {
	field2: 'Select the sensor make and model',
	field3: 'Enter the make of the sensor',
	field4: 'Enter the model of the sensor',
	field5: 'Enter the serial number of the sensor',
	field6: 'Sensor reading',
	field7: 'Unit of measure',
	field8: 'Installation type',
	field9: 'Pipe size ⌀ (mm)',
	field10: 'Width',
	field11: 'Width unit of measure',
};
const formNameKeyName = 'formName';
const contextFormName = '$' + formNameKeyName;
export const prefillSensorSchema = actionableSchema.shape({
	field2: makeAndModel.required(optionRequiredMessage),
	field3: otherMakeOrModel.requiredWhen('field2', makeAndModelOther, requiredMsg(sensorShortLabels.field3)), // prettier-ignore
	field4: otherMakeOrModel.requiredWhen('field2', makeAndModelOther, requiredMsg(sensorShortLabels.field4)), // prettier-ignore
	field5: serialNumber.required(requiredMsg(sensorShortLabels.field5)),
	// field6 (goes on full sensor schema)
	// field7 (goes on full sensor schema)
	field8: Yup.string().required(optionRequiredMessage),
	field9: size.when(['field8', contextFormName], ([field8Value, parentFormName], schema) => {
		const isRequired =
			field8Value === SystemInstallationConfigurationOption.FullPipe ||
			field8Value === SystemInstallationConfigurationOption.PartiallyFilledPipe;
		const isOnNewOrReplacementMeter = parentFormName === 'form02';
		if (isRequired) {
			schema = schema.required(requiredMsg(sensorShortLabels.field9));
			if (isOnNewOrReplacementMeter) {
				schema = schema.min(
					modularMeteringSystemSizeMinValue,
					modularMeteringConfigErrorMsg(modularMeteringSystemSizeMinValue),
				);
			}
		} else {
			return schema.notRequired();
		}
		return schema;
	}),
	field10: Yup.mixed().when(['field8'], ([field8Value], schema) => {
		const isRequired = field8Value === SystemInstallationConfigurationOption.OpenChannel;
		return isRequired
			? widthValue.required(requiredMsg(sensorShortLabels.field10))
			: schema.notRequired();
	}),
	field11: Yup.string().when(['field8'], ([field8Value], schema) => {
		const isRequired = field8Value === SystemInstallationConfigurationOption.OpenChannel;
		return isRequired
			? schema.required(requiredMsg(sensorShortLabels.field11))
			: schema.notRequired();
	}),
});
export const sensorSchema = prefillSensorSchema.shape({
	field6: Yup.mixed().when('action', {
		is: (val: UpdateAction) => val !== 'Remove',
		then: () => readingValue.required(requiredMsg(sensorShortLabels.field6)),
		otherwise: schema => schema.nullable(), // skip validation entirely
	}),
	field7: Yup.mixed().when('action', {
		is: (val: UpdateAction) => val !== 'Remove',
		then: () => readingUnit.required(optionRequiredMessage),
		otherwise: schema => schema.nullable(), // skip validation entirely
	}),
});

// Subform 02 - New Meter
export interface ModuleAForm02Values {
	// field1?: string; - authorisation number field has been removed from the form
	field2: string;
	field3: string;
	field4: string;
	field5: string;
	field7?: number;
	field10: string;
	field11: DeviceReadingUnitMeasureOption;
	field12: string;
	field13: string;
	field14: string;
	field15?: SensorFormValues[];
}
export const moduleAForm02ShortLabels: FormLabels<ModuleAForm02Values> = {
	field2: 'Device type',
	field3: 'Make',
	field4: 'Model',
	field5: 'Serial number',
	field7: 'Size (mm)',
	field10: 'Meter reading',
	field11: 'Unit of measure',
	field12: 'Latitude',
	field13: 'Longitude',
	field14: 'Co-ordinate datum',
	field15: 'Added sensors',
};
export const moduleAForm02Labels: FormLabels<ModuleAForm02Values> = {
	field2: 'Select the type of meter being inspected',
	field3: 'Select the meter make',
	field4: 'Select the meter model',
	field5: 'Enter the serial number of the meter',
	field7: 'Size (mm)',
	field10: 'Meter reading',
	field11: 'Unit of measure',
	field12: 'Latitude',
	field13: 'Longitude',
	field14: 'Select the co-ordinate datum used',
	field15: 'Added sensors',
};

export const meterDeviceTypeKeyName = 'meterDeviceType';
const contextMeterDeviceType = '$' + meterDeviceTypeKeyName;
export const moduleAForm02Schema = Yup.object().shape({
	field2: deviceType.required(optionRequiredMessage),
	field3: make.required(optionRequiredMessage),
	field4: model.required(optionRequiredMessage),
	field5: serialNumber.required(requiredMsg(moduleAForm02ShortLabels.field5)),
	field7: size.when(['field2'], ([field2Value], schema) => {
		const isRequired = field2Value === DeviceTypeOption.ClosedConduitMeter;
		return isRequired
			? schema.required(requiredMsg(moduleAForm02ShortLabels.field7))
			: schema.notRequired();
	}),
	field10: Yup.mixed().when('field2', {
		is: DeviceTypeOption.ClosedConduitMeter,
		then: () => readingValue.required(requiredMsg(moduleAForm02ShortLabels.field10)),
		otherwise: schema => schema.nullable(), // skip validation entirely; mixed needs to be nullable to allow undefined
	}),
	field11: Yup.mixed().when('field2', {
		is: DeviceTypeOption.ClosedConduitMeter,
		then: () => readingUnit.required(optionRequiredMessage),
		otherwise: schema => schema.nullable(), // skip validation entirely
	}),
	field12: Yup.string().required(requiredMsg(moduleAForm02ShortLabels.field12)).matches(latitudeRegExp, latitudeMessage), // prettier-ignore
	field13: Yup.string().required(requiredMsg(moduleAForm02ShortLabels.field13)).matches(longitudeRegExp, longitudeMessage), // prettier-ignore
	field14: Yup.string().required(optionRequiredMessage),
	field15: Yup.array().when(contextMeterDeviceType, {
		is: DeviceTypeOption.ModularMeteringSystem,
		then: schema =>
			schema
				.of(sensorSchema)
				.max(moduleAForms02And03List2Max, maxNumberErrorMsg(moduleAForms02And03List2Max, 'sensors'))
				.test('min-active-sensor', sensorRequiredErrorMsg, function (value) {
					return value && value.filter(sensor => sensor.action !== UpdateAction.REMOVE).length >= 1;
				}),
		otherwise: schema => schema,
	}),
});

// Subform 03_0 (main subform)
export interface ModuleAForm03Values {
	field1: boolean;
	field2: string;
	field3: string;
	field4: string;
	field5: string;
	field6: string;
	field7: string;
	field9?: number;
	field12: string;
	field13: DeviceReadingUnitMeasureOption;
	field14: string;
	field15: string;
	field16: string;
	field17: SensorFormValues[];
	field18: SensorFormValues[];
}
export const moduleAForm03ShortLabels: FormLabels<ModuleAForm03Values> = {
	field1: 'Information accuracy',
	field2: 'Device type',
	field3: 'Make',
	field4: 'Other make',
	field5: 'Model',
	field6: 'Other model',
	field7: 'Serial number',
	field9: 'Size (mm)',
	field12: 'Meter reading',
	field13: 'Unit of measure',
	field14: 'Latitude',
	field15: 'Longitude',
	field16: 'Co-ordinate datum',
	field17: 'Existing sensors', // to edit existing modular metering system sensors
	field18: 'Added sensors', // to add more modular metering system sensors
};
export const moduleAForm03Labels: FormLabels<ModuleAForm03Values> = {
	field1: 'Is this information accurate?',
	field2: 'Select the type of meter',
	field3: 'Select the meter make',
	field4: 'Enter the make of the meter',
	field5: 'Select the meter model',
	field6: 'Enter the model of the meter',
	field7: 'Enter the serial number of the meter',
	field9: 'Size (mm)',
	field12: 'Meter reading',
	field13: 'Unit of measure',
	field14: 'Latitude',
	field15: 'Longitude',
	field16: 'Select the co-ordinate datum used',
	field17: 'Existing sensors', // to edit existing modular metering system components
	field18: 'Added sensors', // to add more modular metering system components
};

export const moduleAForm03SummaryLabels: FormLabels<ModuleAForm03Values> = {
	field1: 'Is this information accurate?',
	field2: 'Meter',
	field3: 'Make',
	field4: 'Make (other)',
	field5: 'Model',
	field6: 'Model (other)',
	field7: 'Serial number',
	field9: 'Size (mm)',
	field12: 'Meter reading',
	field13: 'Unit of measure',
	field14: 'Latitude',
	field15: 'Longitude',
	field16: 'Datum',
	field17: 'Existing sensors',
	field18: 'Added sensors',
};

// TODO: accuracy value ('field1') might not sense any more. Besides, fields required when any 'field1' or contextAccuracyRules could set straight as required
/*
# Subform 03 - Conditions of visibility rules (conditional required)
	Condition dataHasExistingApproved: meter type/make/mode has as "existing approved  make/model" and more
*/
export const isValidOnAccuracyRulesKeyName = 'isValidOnAccuracyRules';
const contextAccuracyRules = '$' + isValidOnAccuracyRulesKeyName;
export const moduleAForm03Schema = Yup.object().shape({
	field1: Yup.boolean().notRequired(),
	field2: deviceType.requiredWhenAny(['field1', contextAccuracyRules], [false, false], optionRequiredMessage), // prettier-ignore
	field3: make.requiredWhenAny(
		['field1', contextAccuracyRules],
		[false, false],
		optionRequiredMessage,
	),
	field4: otherMakeOrModel.requiredWhen( 'field3', makeOther, requiredMsg(moduleAForm03ShortLabels.field4)), // prettier-ignore
	field5: model.requiredWhenAny( ['field1', contextAccuracyRules], [false, false], optionRequiredMessage), // prettier-ignore
	field6: otherMakeOrModel.requiredWhen( 'field5', modelOther, requiredMsg(moduleAForm03ShortLabels.field6)), // prettier-ignore
	field7: serialNumber.requiredWhenAny( ['field1', contextAccuracyRules], [false, false], requiredMsg(moduleAForm03ShortLabels.field7)), // prettier-ignore
	field9: size.when(
		['field1', contextAccuracyRules, 'field2'],
		([field1Value, specialCondValue, field2Value], schema) => {
			const isRequiredByAccuracy = field1Value === false || specialCondValue === false;
			const isRequiredByConduitMeter =
				isRequiredByAccuracy && field2Value === DeviceTypeOption.ClosedConduitMeter;
			if (isRequiredByConduitMeter) {
				return schema.required(requiredMsg(moduleAForm03ShortLabels.field9));
			} else {
				return schema.notRequired();
			}
		},
	),
	field12: Yup.mixed().when('field2', {
		is: DeviceTypeOption.ClosedConduitMeter,
		then: () => readingValue.required(requiredMsg(moduleAForm03ShortLabels.field12)),
		otherwise: schema => schema.nullable(), // skip validation entirely
	}),
	field13: Yup.mixed().when('field2', {
		is: DeviceTypeOption.ClosedConduitMeter,
		then: () => readingUnit.required(optionRequiredMessage),
		otherwise: schema => schema.nullable(), // skip validation entirely
	}),
	field14: Yup.string().transform(emptyToUndefined).matches(latitudeRegExp, latitudeMessage).requiredWhenAny(['field1', contextAccuracyRules], [false, false], requiredMsg(moduleAForm03ShortLabels.field14)), // prettier-ignore
	field15: Yup.string().transform(emptyToUndefined).matches(longitudeRegExp, longitudeMessage).requiredWhenAny(['field1', contextAccuracyRules], [false, false], requiredMsg(moduleAForm03ShortLabels.field15)), // prettier-ignore
	field16: Yup.string().requiredWhenAny(
		['field1', contextAccuracyRules],
		[false, false],
		optionRequiredMessage,
	),
	field17: Yup.array() // visibility rules on layout only (when Cond_C and field2 === 'M.M.S')
		.when(contextMeterDeviceType, {
			is: DeviceTypeOption.ModularMeteringSystem,
			then: schema => schema.of(sensorSchema),
			otherwise: schema => schema,
		}),
	field18: Yup.array() // visibility rules on layout only (when Cond_C and field2 === 'M.M.S')
		.when(contextMeterDeviceType, {
			is: DeviceTypeOption.ModularMeteringSystem,
			then: schema =>
				schema
					.of(sensorSchema)
					.max(
						moduleAForms02And03List2Max,
						maxNumberErrorMsg(moduleAForms02And03List2Max, 'sensors'),
					)
					.test('min-active-sensor', sensorRequiredErrorMsg, function (value, context) {
						const { field17 } = context.parent;
						const allSensors = [...(field17 ?? []), ...(value ?? [])];
						return allSensors.filter(sensor => sensor.action !== UpdateAction.REMOVE).length >= 1;
					}),
			otherwise: schema => schema,
		}),
});

// Subform 04_1 (for field as nested array form)
export interface TamperSealFormValues extends ActionableFormValues {
	field1: string;
}
export type TamperSealFormValuesInFormArray = SensorFormValues & { id: string };
export const tamperSealShortLabels: FormLabels<
	Omit<TamperSealFormValues, keyof ActionableFormValues>
> = {
	field1: 'Seal ID number',
};
export const tamperSealLabels: FormLabels<Omit<TamperSealFormValues, keyof ActionableFormValues>> =
	{
		field1: 'Seal ID number',
	};
export const moduleAForms04ListMax = 40;
export const tamperSealSchema: Yup.ObjectSchema<TamperSealFormValues> = actionableSchema.shape({
	field1: serialNumber.required(requiredMsg(tamperSealLabels.field1)),
});

// Subform 04 - Tamper Seals
export type ModuleAForm04Values = NestedArrayForm<TamperSealFormValues>;
export const moduleAForm04Schema = Yup.object().shape({
	list: Yup.array()
		.of(tamperSealSchema)
		.test('min-active-nested-seals', 'At least one seal must be added.', function (value) {
			return value && value.filter(seal => seal.action !== UpdateAction.REMOVE).length >= 1;
		})
		.max(moduleAForms04ListMax, `Maximum of ${moduleAForms04ListMax} seals can be added.`),
});
export const schemaFieldArrayName = 'list';

// Subform 05 - Photos
export type ActionablePhoto = Photo & ActionableFormValues;

export interface ModuleAForm05Values {
	field1: FileUploadItem[];
	field2: FileUploadItem[];
	field3: FileUploadItem[];
	field4: FileUploadItem[];
	field5: FileUploadItem[];
	field6?: FileUploadItem[];
	field7?: FileUploadItem[];
}
export const moduleAForm05Labels: FormLabels<ModuleAForm05Values> = {
	field1: 'Photograph of the meter serial number',
	field2: 'Photograph of the meter reading',
	field3: 'Photograph of the upstream extent',
	field4: 'Photograph of the downstream extent',
	field5: 'Photograph of the metering site',
	field6: 'Photograph of final meter reading of the disposed meter',
	field7: 'Photograph of the meter serial number of the disposed meter',
};

export const moduleAForm05Schema = Yup.object().shape({
	field1: FileUploadSchema('image', imageRequiredErrorMsg),
	field2: FileUploadSchema('image', imageRequiredErrorMsg),
	field3: FileUploadSchema('image', imageRequiredErrorMsg),
	field4: FileUploadSchema('image', imageRequiredErrorMsg),
	field5: FileUploadSchema('image', imageRequiredErrorMsg),
	field6: FileUploadOptionalSchema('image'),
	field7: FileUploadOptionalSchema('image'),
});

// Subform 06 - Declaration
export interface ModuleAForm06Values {
	field1: Date | DateValue;
	field2: InspectionOutcomeOption;
	field3?: DocumentsForClientOption[];
	field4?: string;
	field5?: string;
}
export const moduleAForm06ShortLabels: FormLabels<ModuleAForm06Values> = {
	field1: 'Date of inspection',
	field2: 'Actions as certified meter installer',
	field3: 'Documents for client',
	field4: 'Explanation of measurement device difference from standard',
	field5: 'Modifications to meet standards',
};
export const moduleAForm06Labels: FormLabels<ModuleAForm06Values> = {
	field1: 'Inspection date',
	field2: 'I am submitting a:',
	field3: 'I will provide a copy of the compliance certificate and the following documentation to the client (select as applicable) within 10 business days of the date of inspection:', // prettier-ignore
	field4: 'Explain how the meter does not comply with the Queensland Non-Urban Water Measurement Standards', // prettier-ignore
	field5: 'Explain what modifications are required for the meter to comply with the Queensland Non-Urban Water Measurement Standards', // prettier-ignore
};

export const moduleAForm06SummaryLabels: FormLabels<ModuleAForm06Values> = {
	field1: 'Inspection date:',
	field2: 'Outcome:',
	field3: '',
	field4: 'Non-compliance particulars:',
	field5: 'Modifications required:',
};
export const moduleAForm06Schema = Yup.object().shape({
	field1: Yup.mixed<Date | DateValue>()
		.required(dateErrorMsg)
		.test('max-calendar-date', 'Date cannot be in the future', value => {
			const selectedDate =
				value instanceof Date ? convertDateToCalendarDate(value) : (value as DateValue);
			const maxDateValue = value instanceof Date ? new Date() : MAX_INSPECTION_CALENDAR_DATE;
			return !!selectedDate && selectedDate <= maxDateValue;
		}),
	field2: Yup.mixed<InspectionOutcomeOption>().required(optionRequiredMessage),
	field3: Yup.array().of(Yup.mixed<DocumentsForClientOption>()),
	field4: Yup.string()
		.transform(emptyToUndefined)
		.when('field2', {
			is: (val: InspectionOutcomeOption) =>
				[InspectionOutcomeOption.Option2, InspectionOutcomeOption.Option3].includes(val),
			then: schema =>
				schema
					.required(explanationErrorMsg(explanationMaxLength))
					.min(explanationMinLength, minLengthErrorMsg(explanationMinLength))
					.max(explanationMaxLength, maxLengthErrorMsg(explanationMaxLength))
					.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage),
			otherwise: schema => schema.notRequired(),
		}),
	field5: Yup.string()
		.transform(emptyToUndefined)
		.notRequired()
		.when('field2', {
			is: (val: InspectionOutcomeOption) => val === InspectionOutcomeOption.Option2,
			then: schema =>
				schema
					.required(explanationErrorMsg(explanationMaxLength))
					.min(explanationMinLength, minLengthErrorMsg(explanationMinLength))
					.max(explanationMaxLength, maxLengthErrorMsg(explanationMaxLength))
					.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage),
			otherwise: schema => schema.notRequired(),
		})
		.notRequired(),
});

// Subform 07 - Review
export interface ModuleAForm07Values {
	field1: boolean;
}
export const moduleAForm07Labels: FormLabels<ModuleAForm07Values> = {
	field1:
		'By checking this box, I agree that I have read and understood all of the information above',
};
export const moduleAForm07Schema = Yup.object().shape({
	field1: Yup.boolean()
		.required(finalDeclarationRequiredMsg)
		.test('isTrue', finalDeclarationRequiredMsg, value => value === true),
});

// PARENT FORM (MODULE A FORM)
export interface ModuleAFormValues {
	form01: Partial<ModuleAForm01Values>;
	form02: ModuleAForm02Values;
	form03: ModuleAForm03Values;
	form04: NestedArrayForm<TamperSealFormValues>;
	form05: ModuleAForm05Values;
	form06: ModuleAForm06Values;
	form07: ModuleAForm07Values;
}
export const moduleAFormSchema = Yup.object().shape({
	form01: Yup.object().notRequired(), // do not validate initial subform on parent form
	form02: Yup.object().when('form01.field2', {
		is: (val: ReasonForInspectionOption) => val === ReasonForInspectionOption.NewMeter,
		then: () => moduleAForm02Schema,
		otherwise: schema => schema.notRequired(),
	}),
	form03: Yup.object().when('form01.field2', {
		is: (val: ReasonForInspectionOption) => val === ReasonForInspectionOption.ExistingMeter,
		then: () => moduleAForm03Schema,
		otherwise: schema => schema.notRequired(),
	}),
	form04: moduleAForm04Schema,
	form05: moduleAForm05Schema,
	form06: moduleAForm06Schema,
	form07: moduleAForm07Schema,
});

// HELPER TYPES AND FUNCTIONS
/**
 * nameof operator for type safety of "stringly typed" property names onto the parent form
 */
const nameofFactory =
	<T>() =>
	(name: keyof T) =>
		name;
export const nameofSubform = nameofFactory<ModuleAFormValues>();
export type ModuleASubformName = keyof ModuleAFormValues;
export type FormContextFormName = { [formNameKeyName]: ModuleASubformName };

// FORM CONTEXTS
export interface Form01Context extends FormContextFormName {
	[hasOutcomeKeyName]?: boolean;
	[hasLinkedFormKeyName]?: boolean;
	[caseTypeKeyName]?: MCCSummaryItem.caseType;
}
export interface Form02Context extends FormContextFormName, MeterDeviceFormContext {}
export interface Form03Context
	extends FormContextFormName,
		MeterDeviceFormContext,
		Form03ExternalData {}
export interface Form03ExternalData {
	existingDeviceFormValues?: ModuleAForm03Values;
	[isValidOnAccuracyRulesKeyName]?: boolean;
	isApprovedOrExistingApproved?: boolean;
	[meterDeviceTypeKeyName]?: DeviceTypeOption;
}
interface MeterDeviceFormContext {
	[meterDeviceTypeKeyName]?: DeviceTypeOption;
}
